/**
 * Giving a desktop app its key, with nothing for the user to compare.
 *
 * The app opens this page itself, at the end of the device sign-in flow, with
 * its public key in the **URL fragment**:
 *
 *     https://cloud.mauvely.com/authorise#dk=<spki>&client=snap
 *
 * A fragment is never sent in the HTTP request, so the key this page wraps to
 * came from the app and not from the server — which is exactly the property the
 * old fingerprint comparison was buying, and it is the same property
 * `crypto/share.ts` already relies on for link secrets. Migration 0026 argues
 * it; `worker/routes/devices.ts` holds the server half.
 *
 * ── Why the browser registers the device ────────────────────────────────────
 *
 * The ordering forces it. An app has no session until the device link
 * completes, so at the moment the user is standing here there is no row to
 * approve — the app cannot have made one. So this page registers it on the
 * app's behalf and approves it in the same breath. Registration is idempotent
 * on the fingerprint, so when the app later calls it with its own session it
 * finds this row rather than queuing a second.
 *
 * ── An app-scoped link needs no password, and a vault-scoped one does ───────
 *
 * The live vault's master key is imported non-extractable, and ECIES-wrapping
 * needs the bytes — which is why the old approval screen asks for the password
 * again. An app-scoped device is never handed the master key: this page mints a
 * fresh node key, which `generateAesKey` makes extractable precisely so it can
 * be wrapped. So there is nothing to prompt for.
 *
 * The hub and Relay are the exception, and the exception is the whole reason it
 * is one: they get the master key, so linking either still asks. That asymmetry
 * is worth leaving visible rather than smoothing over — it is what scoping buys.
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import { api } from '../api';
import { requireVault } from '../crypto/vault';
import {
  fingerprintOfSpki,
  generateNodeKey,
  unwrapNodeKey,
  wrapNodeKey
} from '../crypto/keys';
import { encryptName } from '../crypto/metadata';
import { wrapKeyForRecipient } from '../crypto/share';
import { unlockForRewrap } from '../crypto/keys';
import { ds } from '../ds';
import { Footnote, Panel } from '../components/Shell';
import { appName as nameOf, authoriseScope } from '../lib/appscope';

interface Request {
  publicKey: string;
  client: string;
  label: string | null;
}

/** Read the fragment once. Anything that navigates would take it with it. */
function readFragment(): Request | null {
  const hash = window.location.hash.replace(/^#/, '');
  if (!hash) return null;
  const params = new URLSearchParams(hash);
  const publicKey = params.get('dk');
  const client = params.get('client');
  if (!publicKey || !client) return null;
  return { publicKey, client, label: params.get('label') };
}

type Stage = 'working' | 'password' | 'done' | 'error';

export function AuthoriseApp() {
  const { Button, Input } = ds();
  // Captured on the first render and never re-read: `location.hash` is the only
  // copy of the key, and it survives exactly as long as this page does.
  const request = useRef<Request | null>(readFragment()).current;

  const [stage, setStage] = useState<Stage>('working');
  const [error, setError] = useState<string | null>(null);
  const [password, setPassword] = useState('');
  const started = useRef(false);

  // Which key, if any, is `lib/appscope.ts`'s decision. The lists live there so
  // a test can hold them against the Worker's, which this page once disagreed
  // with about Relay.
  const scope = request ? authoriseScope(request.client) : null;
  const appName = request ? nameOf(request.client) : '';

  const run = useCallback(
    async (vaultPassword?: string) => {
      if (!request || !scope) return;
      setError(null);
      setStage('working');
      try {
        const fingerprint = await fingerprintOfSpki(request.publicKey);
        const { id, state } = await api.vault.devices.register({
          publicKey: request.publicKey,
          fingerprint,
          client: request.client,
          label: request.label ?? undefined,
          scope
        });

        // This account approved this key before. `register` answers the row it
        // already has, and `approve` takes only a pending one — so going on
        // asked for a password and then said "That device is not waiting for
        // approval." to an app that was linked all along. The app collects its
        // key on its next try; there is nothing left to do here.
        if (state === 'active') {
          setStage('done');
          return;
        }

        if (scope === 'app') {
          // A fresh key for this app's own tree. Wrapped twice: under the master
          // key so the web app and your other machines can still read it, and to
          // the app so it can read it at all. Migration 0026 on why both.
          const vault = requireVault();
          const minted = await generateNodeKey();
          const { rootNodeId } = await api.vault.devices.createRoot(id, {
            nodeKeyWrapped: await wrapNodeKey(vault.masterKey, minted),
            nameEnc: await encryptName(minted, appName)
          });

          // Read the folder back rather than assuming `minted` opens it.
          //
          // `createRoot` is **idempotent**: a device row that already has a root
          // keeps it and answers with its id, ignoring the key just sent. So on
          // a second link — a half-finished attempt, an app that registered and
          // then took another path — approving with the minted key hands the app
          // a key that opens nothing. Every child then fails to unwrap and the
          // folder reads as empty, which looks like the app is broken rather
          // than like the link is.
          const { node } = await api.nodes.get(rootNodeId);
          const nodeKey = await unwrapNodeKey(vault.masterKey, node.nodeKeyWrapped);

          await api.vault.devices.approve(id, await wrapKeyForRecipient(request.publicKey, nodeKey));
        } else {
          // The hub or Relay, and the master key they need is not extractable
          // in the live vault — so this is the one path that has to ask.
          if (!vaultPassword) {
            setStage('password');
            return;
          }
          const opened = await unlockForRewrap({ password: vaultPassword }, await api.vault.get());
          await api.vault.devices.approve(
            id,
            await wrapKeyForRecipient(request.publicKey, opened.masterKey)
          );
        }
        setStage('done');
      } catch (err) {
        setError(err instanceof Error ? err.message : 'That app could not be linked.');
        setStage('error');
      }
    },
    [request, scope, appName]
  );

  useEffect(() => {
    if (started.current) return;
    started.current = true;
    void run();
  }, [run]);

  if (!request) {
    return (
      <Frame title="Nothing to link">
        <Footnote>Open this from the app you are signing in, not from a bookmark.</Footnote>
      </Frame>
    );
  }

  if (!scope) {
    return (
      <Frame title="That app is not one of ours">
        <Footnote>Mauvely Cloud does not know an app called “{request.client}”.</Footnote>
      </Frame>
    );
  }

  if (stage === 'password') {
    return (
      <Frame title={`Link ${appName}`}>
        <Footnote>{appName} reads every file in your Cloud.</Footnote>
        <Input
          label="Your password"
          type="password"
          value={password}
          onChange={(e) => setPassword((e.target as HTMLInputElement).value)}
        />
        <Button variant="secondary" size="sm" disabled={!password} onClick={() => void run(password)}>
          Link {appName}
        </Button>
        {error ? <Problem>{error}</Problem> : null}
      </Frame>
    );
  }

  if (stage === 'error') {
    return (
      <Frame title={`${appName} was not linked`}>
        <Problem>{error}</Problem>
        <Button variant="secondary" size="sm" onClick={() => void run(password || undefined)}>
          Try again
        </Button>
      </Frame>
    );
  }

  if (stage === 'done') {
    return (
      <Frame title={`${appName} is linked`}>
        <Footnote>
          {scope === 'app'
            ? `It reads and writes its own files. The rest of your Cloud stays out of reach.`
            : `Go back to ${appName}.`}
        </Footnote>
      </Frame>
    );
  }

  return <Frame title={`Linking ${appName}…`} />;
}

function Frame({ title, children }: { title: string; children?: React.ReactNode }) {
  return (
    <div style={{ maxWidth: 460, margin: '48px auto' }}>
      <Panel style={{ padding: '28px 26px', display: 'grid', gap: 12 }}>
        <h1 style={{ margin: 0, fontSize: 19 }}>{title}</h1>
        {children}
      </Panel>
    </div>
  );
}

function Problem({ children }: { children: React.ReactNode }) {
  return <span style={{ color: 'var(--danger)' }}>{children}</span>;
}
